/**
 * `/technicians` — the field technician roster. Port of the demo's
 * `renderTechs()` (line 2719).
 *
 * Who is where, what they are certified for, what they are working on and how
 * the customer rated them — one card each. Under the grid, the dispatch agent's
 * proposed assignments with the reason behind each, and the certificates that
 * stop someone working if they lapse.
 *
 * Ratings are measured rather than surveyed alone: fix-first-time comes from
 * repeat work orders on the same machine and component inside 60 days,
 * utilisation from the timesheets, and the CSS figure from the in-app survey.
 * All three are `techStats()` in `shared/oem-engine.ts`.
 *
 * ## Data
 *
 * Four domains: `people` (presence, the columnar `roster` and `css` tables, the
 * directory), `service` (technician-app jobs, LMS), `work-orders` (the
 * fix-first-time window and the drafted orders the dispatcher has to place) and
 * `costs` (timesheets).
 *
 * ## Declared omissions
 *
 * - **A technician's photo.** The demo has none either; the avatar is initials.
 * - **Assigning for real.** "Assign" toasts, as the demo's button does. There is
 *   no write path to the technician app in a proof of concept.
 *
 * @see src/versions/v1/modules/service/TechniciansScene.transform.ts
 * @see docs/logic/workflows/technicians.md
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { unpackTable } from '../../shared/columnar';
import { useToast } from '../../shared/toast/ToastContext';
import { SceneSection, SourceLink, TonePill } from '../../shared/oem-tiles';
import {
  useOemData,
  type CustomerSatisfactionResponse,
  type RosterEntry,
  type TrainingRecord,
  type WorkOrder,
} from '../../shared/useOemData';
import { useRowsDrawer, type RowsDrawer } from './source-slice';
import {
  CERTIFICATE_LIMIT,
  dispatchRows,
  expiringCertificates,
  starRating,
  technicianCards,
  technicianSummary,
  type DispatchRow,
  type TechnicianCard,
  type TechniciansCtx,
} from './TechniciansScene.transform';

/** Demo line 2720. The page's standfirst, verbatim. */
const STANDFIRST =
  'Who is where, what they are certified for, what they are working on and how the customer ' +
  'rated them — from the roster, the LMS, the technician app and the CSS feed. Ratings are ' +
  'measured from outcomes (fix-first-time, hours against quote, CSS) rather than a survey ' +
  "alone. Dispatch proposals below carry the agent's reason; the planner decides.";

export function TechniciansScene() {
  const [params] = useSearchParams();
  const selected = params.get('technician');

  const people = useOemData('people');
  const service = useOemData('service');
  const workOrders = useOemData('work-orders');
  const costs = useOemData('costs');

  const ctx = useMemo<TechniciansCtx | null>(() => {
    if (!people.data || !service.data || !workOrders.data || !costs.data) return null;
    return {
      techs: people.data.tapp_techs,
      people: people.data.people,
      roster: unpackTable<RosterEntry>(people.data.roster),
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
      techJobs: service.data.tapp_jobs,
      training: service.data.lms,
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      timesheets: costs.data.timesheets,
    };
  }, [people.data, service.data, workOrders.data, costs.data]);

  const cards = useMemo(() => (ctx ? technicianCards(ctx) : []), [ctx]);
  const dispatch = useMemo(() => (ctx ? dispatchRows(ctx) : []), [ctx]);
  const certificates = useMemo(() => (ctx ? expiringCertificates(ctx) : []), [ctx]);
  const summary = useMemo(
    () =>
      ctx
        ? technicianSummary(ctx, cards)
        : { onSite: 0, total: 0, meanUtilisation: 0, openJobs: 0, notCurrent: 0, trainingRecords: 0 },
    [ctx, cards],
  );

  const openRows = useRowsDrawer();
  const error = people.error ?? service.error ?? workOrders.error ?? costs.error;
  const loading = !ctx && !error;

  return (
    <SurfacePageLayout width="full">
      <Commentable name="TechniciansScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-8" as="div">
            <SurfacePageHeader
              kicker="Workflows"
              title="Technicians"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                    {summary.onSite} on site · {summary.total} technicians · W37 roster
                  </span>
                  <HelpAnchor doc="workflows/technicians" />
                </div>
              }
            />

            <AskStrip view="techs" />

            {error ? (
              <InlineError>{`Technician data did not load: ${error.message}`}</InlineError>
            ) : loading ? (
              <SkeletonCardGrid count={6} />
            ) : cards.length === 0 ? (
              <EmptyState
                icon={<Wrench className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
                title="No technicians"
                description="The technician-app roster is empty."
              />
            ) : (
              <>
                <Commentable name="TechnicianRoster">
                  <div className="min-w-0">
                    <SceneSection
                      title="Roster"
                      subtitle={`mean utilisation ${summary.meanUtilisation}% · ${summary.openJobs} open jobs`}
                    >
                      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2 @6xl:grid-cols-3">
                        {cards.map((card) => (
                          <TechnicianProfile
                            key={card.technician}
                            card={card}
                            selected={selected === card.technician}
                            openRows={openRows}
                          />
                        ))}
                      </div>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="DispatchProposals">
                  <div className="min-w-0">
                    <DispatchTable rows={dispatch} />
                  </div>
                </Commentable>

                <Commentable name="CertificatesToRenew">
                  <div className="min-w-0">
                    <CertificateTable
                      rows={certificates}
                      notCurrent={summary.notCurrent}
                      total={summary.trainingRecords}
                      openRows={openRows}
                    />
                  </div>
                </Commentable>
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** Demo line 2721. One technician's card. */
function TechnicianProfile({
  card,
  selected,
  openRows,
}: {
  card: TechnicianCard;
  selected: boolean;
  openRows: RowsDrawer;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selected]);

  const rating = starRating(card.stats.cssAvg);

  return (
    <article
      ref={ref}
      aria-current={selected ? 'true' : undefined}
      className="flex min-w-0 flex-col gap-2.5 rounded-2xl border p-4"
      style={{
        borderColor: selected ? 'var(--accent)' : 'var(--hairline)',
        boxShadow: selected ? '0 0 0 1px var(--accent)' : undefined,
        background: 'var(--surface-2)',
      }}
    >
      <header className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="mono flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
        >
          {card.initials}
        </span>
        <div className="min-w-0">
          <h3
            className="text-[14.5px] font-semibold tracking-tight"
            style={{ color: 'var(--ink-primary)' }}
          >
            {card.technician}
          </h3>
          <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            {card.role}
            {card.grade ? ` · grade ${card.grade}` : null}
          </p>
        </div>
        <span className="ml-auto flex-shrink-0">
          <TonePill tone={card.onSite ? 'good' : 'flat'}>{card.presence.status}</TonePill>
        </span>
      </header>

      <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
        <b>{card.base}</b>
        {card.rosterLine.slice(card.base.length)}
      </p>
      <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
        {card.workLine}
      </p>
      <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
        Rating{' '}
        {rating ? (
          <>
            <span aria-hidden="true" style={{ color: 'var(--warn)' }}>
              {rating.stars}
            </span>{' '}
            <b>{rating.label}</b>
            <span style={{ color: 'var(--ink-tertiary)' }}> ({card.stats.cssN} CSS)</span>
          </>
        ) : (
          <span style={{ color: 'var(--ink-tertiary)' }}>no responses yet</span>
        )}{' '}
        · fix-first-time <b>{card.stats.fft == null ? '—' : `${card.stats.fft}%`}</b>
      </p>

      <div className="flex flex-wrap gap-1">
        {card.skills.map((chip) => (
          <span
            key={chip.key}
            title={chip.title}
            className="rounded-full px-2 py-px text-[10.5px] leading-[1.6]"
            style={{
              background:
                chip.tone === 'bad'
                  ? 'var(--crit-soft)'
                  : chip.tone === 'warn'
                    ? 'var(--warn-soft)'
                    : 'var(--surface-3)',
              color:
                chip.tone === 'bad'
                  ? 'var(--crit)'
                  : chip.tone === 'warn'
                    ? 'var(--warn)'
                    : 'var(--ink-secondary)',
            }}
          >
            {chip.label}
          </span>
        ))}
      </div>

      {card.preference ? (
        <p className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          Customer preference: {card.preference}
        </p>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
        <SourceLink
          onClick={() =>
            openRows('xreach', card.sources.jobs, {
              filter: `technician=${card.technician}`,
              title: `Technician-app jobs · ${card.technician}`,
            })
          }
        >
          {card.stats.jobs} jobs
        </SourceLink>
        <SourceLink
          onClick={() =>
            openRows('timesheets', card.sources.timesheets, {
              filter: `technician=${card.technician}`,
              title: `Timesheet W36 · ${card.technician}`,
            })
          }
        >
          timesheet
        </SourceLink>
        <SourceLink
          onClick={() =>
            openRows('lms', card.stats.lms, {
              filter: `person=${card.technician}`,
              title: `Training records · ${card.technician}`,
            })
          }
        >
          {card.stats.lms.length} training records
        </SourceLink>
        <SourceLink
          onClick={() =>
            openRows('css', card.sources.css, {
              filter: `Technician=${card.technician}`,
              title: `Customer satisfaction · ${card.technician}`,
            })
          }
        >
          {card.stats.cssN} CSS
        </SourceLink>
      </div>
    </article>
  );
}

/** Demo line 2728. The dispatch proposals. */
function DispatchTable({ rows }: { rows: readonly DispatchRow[] }) {
  const { show } = useToast();
  const [assigned, setAssigned] = useState<ReadonlySet<string>>(() => new Set());

  return (
    <SceneSection
      title="Dispatch proposals"
      subtitle="agent-drafted, planner decides"
      actions={
        <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {rows.length} open jobs and drafts
        </span>
      }
    >
      <Table className="min-w-[820px]">
          <THead>
            <TR>
              <TH>Job / draft</TH>
              <TH>Machine</TH>
              <TH>Site</TH>
              <TH>Proposed</TH>
              <TH>Why</TH>
              <TH>
                <span className="sr-only">Assign</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.key}>
                <TD className="mono whitespace-nowrap">{row.ref}</TD>
                <TD>
                  <Link
                    to={`/machines/${row.machine}`}
                    className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
                    style={{ color: 'var(--accent)' }}
                  >
                    {row.machine}
                  </Link>
                </TD>
                <TD className="whitespace-nowrap">{row.siteShort}</TD>
                <TD>
                  <b>{row.best}</b>
                  {row.alt ? (
                    <div className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                      alt. {row.alt}
                    </div>
                  ) : null}
                </TD>
                <TD className="text-[11.5px]">{row.why || '—'}</TD>
                <TD>
                  <button
                    type="button"
                    disabled={assigned.has(row.key)}
                    onClick={() => {
                      setAssigned((prev) => new Set(prev).add(row.key));
                      show('Assignment pushed to the technician app and the roster');
                    }}
                    className="rounded-lg border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] disabled:cursor-default disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
                  >
                    {assigned.has(row.key) ? 'Assigned' : 'Assign'}
                  </button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
    </SceneSection>
  );
}

/** Demo line 2729. Certificates to renew. */
function CertificateTable({
  rows,
  notCurrent,
  total,
  openRows,
}: {
  rows: readonly TrainingRecord[];
  notCurrent: number;
  total: number;
  openRows: RowsDrawer;
}) {
  const shown = rows.slice(0, CERTIFICATE_LIMIT);
  return (
    <SceneSection
      title="Certificates to renew"
      subtitle="LMS"
      actions={
        <SourceLink onClick={() => openRows('lms', rows, { filter: 'status≠Current', total })}>
          all training records
        </SourceLink>
      }
    >
      <Table className="min-w-[620px]">
          <THead>
            <TR>
              <TH>Person</TH>
              <TH>Course</TH>
              <TH>Expires</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {shown.map((x) => (
              <TR key={x.lms_id}>
                <TD>
                  {x.person}
                  <div className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                    {x.org}
                  </div>
                </TD>
                <TD className="text-[11.5px]">{x.course}</TD>
                <TD className="mono whitespace-nowrap">{x.expires}</TD>
                <TD>
                  {/* The shipped pack records only `Expiring`; see the
                      transform's note on the demo's dropped `Expired` branch. */}
                  <span className="band-pill band-fair">{x.status}</span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      <p className="mt-2 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
        {notCurrent} of {total} records not current across technicians, operators and supervisors.
      </p>
    </SceneSection>
  );
}
